import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import * as React from "react"

import { cn } from "~/common/cn"
import { textVariant } from "./app-text"

const buttonVariants = cva(
  "font-md-nichrome inline-flex items-center justify-center whitespace-nowrap rounded-full text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        primary: "bg-base-black hover:bg-base-black/90 !text-base-white",
        secondary: "bg-white text-base-black border border-base-black",
        borderless: "bg-white text-base-black border-none",
        destructive:
          "bg-destructive !text-base-white hover:bg-destructive/90 disabled:bg-destructive/50",
        neutral: "bg-neutral-200 text-base-black hover:bg-neutral-200/90",

        // deprecated
        default: "bg-primary text-primary-foreground uppercase hover:bg-primary/90",
        ghost: "hover:opacity-75 hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        small: `rounded-md px-3 py-1.5 ${textVariant["hairline-2"]}`,
        medium: `rounded-full px-4 py-2.5 ${textVariant["hairline-1"]}`,
        large: `rounded-full px-6 py-3 ${textVariant["h3"]}`,

        // deprecated
        default: "h-12 px-6 py-2",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "medium",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
